export interface EmailTemplateStep {
  step: number;
  subject: string;
  body: string;
  delayDays?: number;
  delayHours?: number;
}

export interface EmailTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  thumbnail?: string;
  steps: EmailTemplateStep[];
}

// Professional HTML email templates for roofing contractors
// Mailchimp/Constant Contact quality with hero sections, testimonials, trust badges, multi-CTAs

export const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: "post-storm-response",
    name: "Post-Storm Response Campaign",
    category: "Post-Storm Outreach",
    description: "7-email sequence for immediate storm damage response with urgency and social proof",
    steps: [
      {
        step: 1,
        subject: "⚠️ Free Storm Damage Inspection - {{neighborhood}} Residents",
        delayDays: 0,
        body: `
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
            <!-- Header -->
            <tr>
              <td style="background: #1e40af; padding: 20px; text-align: center;">
                <img src="{{company_logo}}" alt="{{company_name}}" style="max-height: 50px;" />
                <div style="color: white; margin-top: 10px; font-size: 18px;">📞 {{rep_phone}}</div>
              </td>
            </tr>
            
            <!-- Hero Section -->
            <tr>
              <td style="background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%); padding: 40px 20px; text-align: center; color: white;">
                <h1 style="margin: 0 0 10px 0; font-size: 32px;">Storm Just Hit Your Area?</h1>
                <p style="font-size: 18px; margin: 0;">Free Professional Roof Inspection Within 24 Hours</p>
              </td>
            </tr>
            
            <!-- Body Content -->
            <tr>
              <td style="background: white; padding: 40px 30px;">
                <p style="font-size: 16px; line-height: 1.6; color: #333;">Hi {{first_name}},</p>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  We noticed the recent storm that passed through {{neighborhood}} on {{storm_date}}. 
                  High winds and hail can cause hidden damage that leads to leaks and costly repairs if not addressed quickly.
                </p>
                
                <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0;">
                  <strong style="color: #92400e;">⚡ Act Fast:</strong> Insurance claims must be filed within 12 months of the storm date.
                </div>
                
                <h3 style="color: #1e40af; margin-top: 30px;">What We Check (100% Free):</h3>
                <ul style="line-height: 1.8; color: #555;">
                  <li>Shingle damage and missing granules</li>
                  <li>Hail impact assessment</li>
                  <li>Flashing and seal integrity</li>
                  <li>Gutter and downspout damage</li>
                  <li>Interior leak indicators</li>
                </ul>
                
                <!-- Multi-CTA Section -->
                <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                  <tr>
                    <td align="center">
                      <a href="tel:{{rep_phone}}" style="display: inline-block; background: #dc2626; color: white; padding: 16px 40px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 18px; margin: 10px;">
                        📞 Call Now: {{rep_phone}}
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td align="center">
                      <a href="{{schedule_link}}" style="display: inline-block; background: #1e40af; color: white; padding: 16px 40px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 18px; margin: 10px;">
                        📅 Schedule Free Inspection
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="padding-top: 10px;">
                      <a href="{{text_link}}" style="color: #1e40af; text-decoration: underline;">Or text us at {{rep_phone}}</a>
                    </td>
                  </tr>
                </table>
                
                <!-- Trust Badges -->
                <table width="100%" cellpadding="10" style="margin: 30px 0; border-top: 2px solid #e5e7eb; border-bottom: 2px solid #e5e7eb;">
                  <tr>
                    <td align="center" style="padding: 20px;">
                      <div style="display: inline-block; margin: 0 15px; text-align: center;">
                        <div style="font-size: 24px;">⭐⭐⭐⭐⭐</div>
                        <div style="font-size: 12px; color: #666;">500+ Reviews</div>
                      </div>
                      <div style="display: inline-block; margin: 0 15px; text-align: center;">
                        <div style="font-size: 24px;">✓</div>
                        <div style="font-size: 12px; color: #666;">Licensed & Insured</div>
                      </div>
                      <div style="display: inline-block; margin: 0 15px; text-align: center;">
                        <div style="font-size: 24px;">🏆</div>
                        <div style="font-size: 12px; color: #666;">A+ BBB Rating</div>
                      </div>
                    </td>
                  </tr>
                </table>
                
                <p style="font-size: 14px; color: #666; margin-top: 20px;">
                  Best regards,<br>
                  <strong>{{rep_name}}</strong><br>
                  Storm Damage Specialist<br>
                  {{company_name}}
                </p>
              </td>
            </tr>
            
            <!-- Footer -->
            <tr>
              <td style="background: #f3f4f6; padding: 30px; text-align: center; font-size: 12px; color: #666;">
                <p><strong>{{company_name}}</strong></p>
                <p>License #{{license_number}} | {{company_address}}</p>
                <p>📞 {{rep_phone}} | 📧 {{rep_email}}</p>
                <p style="margin-top: 15px;">
                  <a href="{{facebook_url}}" style="color: #1e40af; margin: 0 5px;">Facebook</a> |
                  <a href="{{instagram_url}}" style="color: #1e40af; margin: 0 5px;">Instagram</a> |
                  <a href="{{linkedin_url}}" style="color: #1e40af; margin: 0 5px;">LinkedIn</a>
                </p>
                <p style="margin-top: 15px; font-size: 11px;">
                  <a href="{{unsubscribe_link}}" style="color: #999;">Unsubscribe</a>
                </p>
              </td>
            </tr>
          </table>
        `
      },
      {
        step: 2,
        subject: "We've Already Helped 15 Neighbors on {{street_name}}",
        delayDays: 2,
        body: `
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
            <tr>
              <td style="background: #1e40af; padding: 20px; text-align: center;">
                <img src="{{company_logo}}" alt="{{company_name}}" style="max-height: 50px;" />
              </td>
            </tr>
            
            <tr>
              <td style="padding: 40px 30px; background: white;">
                <h2 style="color: #1e40af; margin-top: 0;">Hi {{first_name}},</h2>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  Just wanted to follow up quickly - we've been working in {{neighborhood}} non-stop since the storm, 
                  and we've already completed <strong>15 inspections on {{street_name}}</strong> alone.
                </p>
                
                <div style="background: #dbeafe; border-radius: 8px; padding: 25px; margin: 25px 0; text-align: center;">
                  <div style="font-size: 48px; color: #1e40af; font-weight: bold;">15/20</div>
                  <div style="font-size: 18px; color: #1e3a8a; margin-top: 10px;">
                    Homes Inspected on Your Street Had Storm Damage
                  </div>
                </div>
                
                <h3 style="color: #dc2626;">🚨 What Happens If You Wait:</h3>
                <ul style="line-height: 1.8; color: #555;">
                  <li>Small leaks become major water damage ($10,000+)</li>
                  <li>Insurance companies become harder to work with</li>
                  <li>Your neighbors file claims first (adjuster fatigue)</li>
                  <li>Storm-related damage gets harder to prove</li>
                </ul>
                
                <!-- Social Proof Block -->
                <div style="background: #f9fafb; border-left: 4px solid #10b981; padding: 20px; margin: 30px 0;">
                  <div style="color: #065f46; font-size: 18px; margin-bottom: 10px;">⭐⭐⭐⭐⭐</div>
                  <p style="font-style: italic; color: #374151; margin: 10px 0;">
                    "{{company_name}} found $18,000 in hail damage that I never would have seen. 
                    Insurance covered everything. They made the whole process so easy!"
                  </p>
                  <p style="color: #6b7280; font-size: 14px; margin: 5px 0;">
                    - Sarah M., {{neighborhood}} Homeowner
                  </p>
                </div>
                
                <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                  <tr>
                    <td align="center">
                      <a href="tel:{{rep_phone}}" style="display: inline-block; background: #dc2626; color: white; padding: 18px 50px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 20px;">
                        Call Right Now: {{rep_phone}}
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="padding-top: 15px;">
                      <a href="{{schedule_link}}" style="display: inline-block; background: #1e40af; color: white; padding: 16px 40px; text-decoration: none; border-radius: 8px; font-weight: bold;">
                        Schedule My Free Inspection
                      </a>
                    </td>
                  </tr>
                </table>
                
                <p style="font-size: 14px; color: #666;">
                  We're in your area all week. I can personally come by as early as tomorrow.<br><br>
                  {{rep_name}}<br>
                  {{rep_phone}}
                </p>
              </td>
            </tr>
            
            <tr>
              <td style="background: #f3f4f6; padding: 30px; text-align: center; font-size: 12px; color: #666;">
                <p><strong>{{company_name}}</strong> | License #{{license_number}}</p>
                <p>{{company_address}} | {{rep_phone}}</p>
                <p style="margin-top: 10px;"><a href="{{unsubscribe_link}}" style="color: #999;">Unsubscribe</a></p>
              </td>
            </tr>
          </table>
        `
      },
      {
        step: 3,
        subject: "📖 The Hidden Storm Damage Most Homeowners Miss",
        delayDays: 5,
        body: `
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: white;">
            <tr>
              <td style="background: #1e40af; padding: 20px; text-align: center;">
                <img src="{{company_logo}}" alt="{{company_name}}" style="max-height: 50px;" />
              </td>
            </tr>
            
            <tr>
              <td style="padding: 40px 30px;">
                <h2 style="color: #1e40af;">Hi {{first_name}},</h2>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  After 20+ years inspecting storm-damaged roofs, I wanted to share what most homeowners 
                  don't know about hidden damage from the recent storm.
                </p>
                
                <h3 style="color: #dc2626; margin-top: 30px;">5 Signs of Hidden Storm Damage:</h3>
                
                <div style="background: #fef2f2; border-radius: 8px; padding: 15px; margin: 15px 0;">
                  <strong style="color: #dc2626;">1. Granule Loss</strong>
                  <p style="color: #555; margin: 5px 0 0 0;">Check your gutters - excessive granules mean your shingles are compromised</p>
                </div>
                
                <div style="background: #fef2f2; border-radius: 8px; padding: 15px; margin: 15px 0;">
                  <strong style="color: #dc2626;">2. Soft Spots on Roof</strong>
                  <p style="color: #555; margin: 5px 0 0 0;">Walking on your roof reveals underlying deck damage not visible from ground</p>
                </div>
                
                <div style="background: #fef2f2; border-radius: 8px; padding: 15px; margin: 15px 0;">
                  <strong style="color: #dc2626;">3. Ceiling Stains (Even Small Ones)</strong>
                  <p style="color: #555; margin: 5px 0 0 0;">Small stains = big problems. Water damage spreads quickly through insulation</p>
                </div>
                
                <div style="background: #fef2f2; border-radius: 8px; padding: 15px; margin: 15px 0;">
                  <strong style="color: #dc2626;">4. Damaged Flashing</strong>
                  <p style="color: #555; margin: 5px 0 0 0;">Around chimneys, vents, and valleys - the #1 source of leaks after storms</p>
                </div>
                
                <div style="background: #fef2f2; border-radius: 8px; padding: 15px; margin: 15px 0;">
                  <strong style="color: #dc2626;">5. Dented Gutters/Downspouts</strong>
                  <p style="color: #555; margin: 5px 0 0 0;">If gutters are dented, your roof definitely has hail damage too</p>
                </div>
                
                <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 20px; margin: 25px 0;">
                  <strong style="color: #92400e;">💡 Pro Tip:</strong> Don't climb on your roof yourself! 
                  It's dangerous and you could void warranties. Let us do the inspection safely.
                </div>
                
                <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                  <tr>
                    <td align="center">
                      <a href="{{schedule_link}}" style="display: inline-block; background: #dc2626; color: white; padding: 18px 50px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 18px;">
                        Get Free Professional Inspection
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="padding-top: 15px;">
                      <a href="tel:{{rep_phone}}" style="color: #1e40af; font-size: 16px; text-decoration: underline;">
                        Or call: {{rep_phone}}
                      </a>
                    </td>
                  </tr>
                </table>
                
                <p style="font-size: 14px; color: #666;">
                  Stay safe,<br>
                  {{rep_name}}<br>
                  Certified Storm Damage Inspector
                </p>
              </td>
            </tr>
            
            <tr>
              <td style="background: #f3f4f6; padding: 30px; text-align: center; font-size: 12px; color: #666;">
                <p><strong>{{company_name}}</strong> | License #{{license_number}}</p>
                <p><a href="{{unsubscribe_link}}" style="color: #999;">Unsubscribe</a></p>
              </td>
            </tr>
          </table>
        `
      },
      {
        step: 4,
        subject: "See How We Helped the Johnson Family (Before/After Photos)",
        delayDays: 7,
        body: `
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: white;">
            <tr>
              <td style="background: #1e40af; padding: 20px; text-align: center;">
                <img src="{{company_logo}}" alt="{{company_name}}" style="max-height: 50px;" />
              </td>
            </tr>
            
            <tr>
              <td style="padding: 40px 30px;">
                <h2 style="color: #1e40af;">Hi {{first_name}},</h2>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  I wanted to share a recent success story from your neighborhood that might resonate with you.
                </p>
                
                <div style="background: #f0fdf4; border: 2px solid #10b981; border-radius: 12px; padding: 30px; margin: 30px 0;">
                  <h3 style="color: #065f46; margin-top: 0; text-align: center;">⭐⭐⭐⭐⭐ Customer Success Story</h3>
                  
                  <p style="font-style: italic; color: #374151; font-size: 16px; line-height: 1.8;">
                    "After the April storm, we noticed a few missing shingles but didn't think it was a big deal. 
                    {{company_name}} came out for a free inspection and found over $22,000 in damage we couldn't see 
                    from the ground. They worked directly with our insurance - State Farm approved the full claim, 
                    and we only paid our $1,500 deductible. The new roof looks amazing and our home value increased!"
                  </p>
                  
                  <p style="color: #065f46; font-weight: bold; margin-top: 20px;">
                    - Mike & Jennifer Johnson<br>
                    {{neighborhood}} Residents Since 2015
                  </p>
                  
                  <div style="background: white; border-radius: 8px; padding: 20px; margin-top: 20px; text-align: center;">
                    <div style="font-size: 36px; color: #10b981; font-weight: bold;">$22,000</div>
                    <div style="font-size: 16px; color: #374151;">Insurance Covered</div>
                    <div style="font-size: 14px; color: #6b7280; margin-top: 5px;">Only $1,500 deductible paid</div>
                  </div>
                </div>
                
                <h3 style="color: #1e40af;">Your Home Deserves the Same Care:</h3>
                <ul style="line-height: 1.8; color: #555; font-size: 16px;">
                  <li>✓ Free professional inspection</li>
                  <li>✓ Insurance claim assistance</li>
                  <li>✓ Premium materials included</li>
                  <li>✓ 50-year warranty</li>
                  <li>✓ A+ BBB rating</li>
                </ul>
                
                <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                  <tr>
                    <td align="center">
                      <a href="{{schedule_link}}" style="display: inline-block; background: #10b981; color: white; padding: 18px 50px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 18px;">
                        Schedule My Free Inspection
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="padding-top: 15px;">
                      <a href="tel:{{rep_phone}}" style="color: #1e40af; font-size: 16px; text-decoration: underline;">
                        Call {{rep_name}}: {{rep_phone}}
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            
            <tr>
              <td style="background: #f3f4f6; padding: 30px; text-align: center; font-size: 12px; color: #666;">
                <p><strong>{{company_name}}</strong> | License #{{license_number}}</p>
                <p><a href="{{unsubscribe_link}}" style="color: #999;">Unsubscribe</a></p>
              </td>
            </tr>
          </table>
        `
      },
      {
        step: 5,
        subject: "⏰ Limited Availability - We're Booking Up Fast",
        delayDays: 10,
        body: `
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
            <tr>
              <td style="background: #1e40af; padding: 20px; text-align: center;">
                <img src="{{company_logo}}" alt="{{company_name}}" style="max-height: 50px;" />
              </td>
            </tr>
            
            <tr>
              <td style="background: #fef2f2; padding: 40px 30px; border-top: 4px solid #dc2626;">
                <h2 style="color: #dc2626; margin-top: 0; text-align: center;">🚨 SCHEDULING NOTICE 🚨</h2>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333; text-align: center;">
                  Hi {{first_name}},
                </p>
                
                <div style="background: white; border: 3px solid #dc2626; border-radius: 12px; padding: 30px; margin: 20px 0; text-align: center;">
                  <div style="font-size: 48px; color: #dc2626; font-weight: bold;">3 Weeks Out</div>
                  <div style="font-size: 20px; color: #374151; margin-top: 10px;">
                    That's our current booking timeline for {{neighborhood}}
                  </div>
                </div>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  Due to overwhelming demand after the storm, we're now scheduling inspections 
                  <strong>3 weeks out</strong>. Every day we get further booked.
                </p>
                
                <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 20px; margin: 25px 0;">
                  <strong style="color: #92400e;">⚠️ Why This Matters:</strong>
                  <ul style="margin: 10px 0; padding-left: 20px; color: #78350f;">
                    <li>Insurance adjusters get "claim fatigue" after handling hundreds of storm claims</li>
                    <li>The longer you wait, the harder it becomes to prove storm-related damage</li>
                    <li>Small leaks discovered now become major problems by the time we can get there</li>
                  </ul>
                </div>
                
                <h3 style="color: #1e40af; text-align: center;">Book Today = Priority Scheduling</h3>
                
                <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                  <tr>
                    <td align="center">
                      <a href="tel:{{rep_phone}}" style="display: inline-block; background: #dc2626; color: white; padding: 20px 60px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 20px;">
                        📞 Call Now: {{rep_phone}}
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="padding-top: 15px;">
                      <a href="{{schedule_link}}" style="display: inline-block; background: #1e40af; color: white; padding: 16px 40px; text-decoration: none; border-radius: 8px; font-weight: bold;">
                        Reserve My Spot Online
                      </a>
                    </td>
                  </tr>
                </table>
                
                <p style="font-size: 14px; color: #666; text-align: center;">
                  Don't let your home be the last one on the block to get checked.<br><br>
                  {{rep_name}}<br>
                  {{company_name}}
                </p>
              </td>
            </tr>
            
            <tr>
              <td style="background: #f3f4f6; padding: 30px; text-align: center; font-size: 12px; color: #666;">
                <p><strong>{{company_name}}</strong> | License #{{license_number}}</p>
                <p><a href="{{unsubscribe_link}}" style="color: #999;">Unsubscribe</a></p>
              </td>
            </tr>
          </table>
        `
      },
      {
        step: 6,
        subject: "Final Chance - Inspection Deadline Approaching",
        delayDays: 14,
        body: `
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
            <tr>
              <td style="background: #1e40af; padding: 20px; text-align: center;">
                <img src="{{company_logo}}" alt="{{company_name}}" style="max-height: 50px;" />
              </td>
            </tr>
            
            <tr>
              <td style="background: white; padding: 40px 30px;">
                <h2 style="color: #dc2626; text-align: center;">⏰ Last Call, {{first_name}}</h2>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  This is my final email about storm damage inspection for {{property_address}}.
                </p>
                
                <div style="background: #fef2f2; border: 2px solid #dc2626; border-radius: 12px; padding: 25px; margin: 25px 0; text-align: center;">
                  <div style="font-size: 18px; color: #991b1b; font-weight: bold;">Storm Date: {{storm_date}}</div>
                  <div style="font-size: 36px; color: #dc2626; font-weight: bold; margin: 15px 0;">346 Days Left</div>
                  <div style="font-size: 16px; color: #7f1d1d;">to file insurance claim</div>
                </div>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  After this email, I'll remove you from our storm outreach list. But I wanted to give you one more chance 
                  because I genuinely believe your roof may have damage that will cost you thousands if not addressed.
                </p>
                
                <h3 style="color: #1e40af;">What You're Leaving on the Table:</h3>
                <table width="100%" cellpadding="15" style="margin: 20px 0;">
                  <tr>
                    <td style="background: #f0fdf4; border-radius: 8px; width: 50%;">
                      <div style="font-size: 28px; color: #10b981; font-weight: bold;">$0</div>
                      <div style="font-size: 14px; color: #065f46;">Free Professional Inspection</div>
                    </td>
                    <td style="background: #f0fdf4; border-radius: 8px; width: 50%;">
                      <div style="font-size: 28px; color: #10b981; font-weight: bold;">$15K-$30K</div>
                      <div style="font-size: 14px; color: #065f46;">Average Storm Claim Value</div>
                    </td>
                  </tr>
                </table>
                
                <div style="background: #dbeafe; border-radius: 8px; padding: 20px; margin: 25px 0;">
                  <p style="margin: 0; color: #1e3a8a; font-size: 16px; line-height: 1.8;">
                    <strong>No obligation. No pressure.</strong> If we don't find claimable damage, you owe nothing. 
                    If we do find damage, we handle the entire insurance process for you.
                  </p>
                </div>
                
                <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                  <tr>
                    <td align="center">
                      <a href="tel:{{rep_phone}}" style="display: inline-block; background: #dc2626; color: white; padding: 20px 60px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 20px;">
                        🔥 Final Chance - Call Now
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="padding: 20px 0;">
                      <div style="font-size: 24px; color: #1e40af; font-weight: bold;">{{rep_phone}}</div>
                    </td>
                  </tr>
                </table>
                
                <p style="font-size: 14px; color: #666;">
                  I hope to hear from you,<br>
                  {{rep_name}}<br>
                  {{company_name}}
                </p>
              </td>
            </tr>
            
            <tr>
              <td style="background: #f3f4f6; padding: 30px; text-align: center; font-size: 12px; color: #666;">
                <p><strong>{{company_name}}</strong> | License #{{license_number}}</p>
                <p><a href="{{unsubscribe_link}}" style="color: #999;">Unsubscribe</a></p>
              </td>
            </tr>
          </table>
        `
      },
      {
        step: 7,
        subject: "Still Interested? We're Here When You're Ready",
        delayDays: 21,
        body: `
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
            <tr>
              <td style="background: #1e40af; padding: 20px; text-align: center;">
                <img src="{{company_logo}}" alt="{{company_name}}" style="max-height: 50px;" />
              </td>
            </tr>
            
            <tr>
              <td style="background: white; padding: 40px 30px;">
                <p style="font-size: 16px; line-height: 1.6; color: #333;">Hi {{first_name}},</p>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  I know I said that was my last email, but I wanted to reach out one more time with a softer approach.
                </p>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  Maybe the timing isn't right. Maybe you're already working with someone else. Maybe you've already had 
                  your roof checked. That's all completely fine.
                </p>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  But if you <strong>haven't</strong> had an inspection yet, and you're just not sure who to trust, 
                  I'd love to earn your business by simply doing what we promised: a free, no-pressure inspection.
                </p>
                
                <div style="background: #f9fafb; border-radius: 8px; padding: 25px; margin: 25px 0; text-align: center;">
                  <p style="font-size: 18px; color: #374151; margin: 0;">
                    📞 My personal cell: <strong style="color: #1e40af;">{{rep_phone}}</strong>
                  </p>
                  <p style="font-size: 14px; color: #6b7280; margin-top: 10px;">
                    Text me anytime - I respond within 30 minutes
                  </p>
                </div>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  If I don't hear from you, I won't follow up again. This is the last you'll hear from me about storm damage.
                </p>
                
                <p style="font-size: 16px; line-height: 1.6; color: #333;">
                  Either way, I hope your home came through the storm in good shape.
                </p>
                
                <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                  <tr>
                    <td align="center">
                      <a href="tel:{{rep_phone}}" style="display: inline-block; background: #1e40af; color: white; padding: 16px 40px; text-decoration: none; border-radius: 8px; font-weight: bold;">
                        Give Us a Call
                      </a>
                    </td>
                  </tr>
                </table>
                
                <p style="font-size: 14px; color: #666;">
                  All the best,<br>
                  {{rep_name}}<br>
                  {{company_name}}<br>
                  {{rep_phone}}
                </p>
              </td>
            </tr>
            
            <tr>
              <td style="background: #f3f4f6; padding: 30px; text-align: center; font-size: 12px; color: #666;">
                <p><strong>{{company_name}}</strong> | License #{{license_number}}</p>
                <p><a href="{{unsubscribe_link}}" style="color: #999;">Unsubscribe</a></p>
              </td>
            </tr>
          </table>
        `
      }
    ]
  },

  // Additional abbreviated templates for other categories
  {
    id: "cold-lead-nurture",
    name: "Cold Lead Nurture Campaign",
    category: "Cold Outreach",
    description: "7-email sequence to warm up cold leads with education and social proof",
    steps: [
      {
        step: 1,
        subject: "Is Your Roof Older Than 15 Years? Here's What You Need to Know",
        delayDays: 0,
        body: `[Professional HTML template - similar structure to above]`
      }
    ]
  }
];

// Utility functions
export function getTemplatesByCategory(category: string): EmailTemplate[] {
  return EMAIL_TEMPLATES.filter(template => template.category === category);
}

export function getAllCategories(): string[] {
  const categories = EMAIL_TEMPLATES.map(template => template.category);
  return Array.from(new Set(categories));
}

export function getTemplateById(id: string): EmailTemplate | undefined {
  return EMAIL_TEMPLATES.find(template => template.id === id);
}
